import type { WorkspaceResponseV2 } from '@emma/api_client/types';
import { Box, Divider, Flex, Icon, Text } from '@lego/klik-ui';
import { PlusBold } from '@lego/klik-ui/icons';
import { useState } from 'react';
import { Loader } from '@/components/common';
import { CreateFleetDialog } from '@/components/fleet/CreateFleetDialog';
import { FleetCard } from '@/components/workspace/FleetCard';
import { useFleetInvitationsByInvitedWorkspace, useFleets } from '@/hooks/fleet';
import { useAllWorkspaces, useCanWriteById } from '@/hooks/workspace';

type Props = {
  workspace: WorkspaceResponseV2;
};

export const WorkspaceFleetsSubPage = (props: Props) => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const canWrite = useCanWriteById(props.workspace.id);
  const { data, error, isLoading } = useFleets(props.workspace.id);

  const {
    data: invitationsData,
    error: invitationsError,
    isLoading: invitationsLoading,
  } = useFleetInvitationsByInvitedWorkspace(props.workspace.id);

  const { data: allWorkspacesData } = useAllWorkspaces();

  if (isLoading || error || !data || invitationsLoading || invitationsError) {
    return <Loader />;
  }

  const invitations = invitationsData?.invitations ?? [];
  const allWorkspaces = allWorkspacesData?.workspaces ?? [];

  const getWorkspaceName = (workspaceId: number) =>
    allWorkspaces.find((w) => w.id === workspaceId)?.name ?? `Workspace #${workspaceId}`;

  const hasOwnedFleets = data.fleets.length > 0;
  const hasInvitedFleets = invitations.length > 0;

  return (
    <Box>
      {hasOwnedFleets && (
        <>
          <Text color="gray.700" fontSize="lg" fontWeight="semibold" mb={4}>
            Owned Fleets
          </Text>
          <Flex flex="1" flexWrap="wrap" gap={4}>
            {data.fleets.map((fleet) => (
              <FleetCard fleet={fleet} key={fleet.id} type="owned" />
            ))}
          </Flex>
        </>
      )}

      {canWrite && (
        <Box
          _hover={{ boxShadow: 'lg', borderColor: 'gray.300' }}
          background="white"
          borderColor="gray.200"
          borderStyle="dashed"
          borderWidth="1px"
          boxShadow="md"
          cursor="pointer"
          mt={4}
          onClick={() => setIsCreateDialogOpen(true)}
          padding={6}
          rounded="md"
          transition="all 0.2s"
          width="100%"
        >
          <Flex alignItems="center" gap={2} justifyContent="center">
            <Icon as={PlusBold} color="gray.500" />
            <Text color="gray.500" fontWeight="medium">
              Create Fleet
            </Text>
          </Flex>
        </Box>
      )}

      {hasOwnedFleets && hasInvitedFleets && <Divider my={6} />}

      {hasInvitedFleets && (
        <>
          <Text color="gray.700" fontSize="lg" fontWeight="semibold" mb={4}>
            Invited Fleets (Cohosting)
          </Text>
          <Flex flex="1" flexWrap="wrap" gap={4}>
            {invitations.map((invitation) => (
              <FleetCard
                invitation={invitation}
                invitedWorkspaceId={props.workspace.id}
                key={invitation.id}
                ownerWorkspaceName={getWorkspaceName(invitation.workspaceId)}
                type="invited"
              />
            ))}
          </Flex>
        </>
      )}

      {!hasOwnedFleets && !hasInvitedFleets && (
        <Box bg="gray.50" borderRadius="md" p={8} textAlign="center">
          <Text color="gray.500">No fleets available for this workspace.</Text>
          <Text color="gray.400" fontSize="sm" mt={2}>
            Create a new fleet or get invited to cohost on another workspace's fleet.
          </Text>
        </Box>
      )}

      <CreateFleetDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        workspaceId={props.workspace.id}
      />
    </Box>
  );
};
